import { AuthForm } from "../components/AuthForm";
import { useUserVerificationQuery, userVerificationService } from "@/common/api/services/userVerification";
import forgotPasswordImage from "@/common/assets/images/forgotPassword.webp";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Mail } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { z } from "zod";

type ForgotPasswordFormValues = {
  email: string;
};

export const ForgotPasswordForm = () => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const forgotPasswordSchema = z.object({
    email: z.string().email(t('ForgotPasswordForm.validation.invalidEmail')),
  });

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const { mutate: resetPassword, isPending } = useMutation({
    mutationFn: (data: ForgotPasswordFormValues) =>
      userVerificationService.generatePasswordReset(data),
    onSuccess: () => {
      toast({
        title: t('ForgotPasswordForm.toast.successTitle'),
        description: t('ForgotPasswordForm.toast.successDescription'),
      });
    },
    onError: (error) => {
      toast({
        title: t('ForgotPasswordForm.toast.errorTitle'),
        description: t('ForgotPasswordForm.toast.errorDescription'),
        variant: "destructive",
      });
      console.error(error);
    },
  });

  const onSubmit = (data: ForgotPasswordFormValues) => {
    resetPassword(data);
  };

  return (
    <AuthForm
      image={forgotPasswordImage}
      imageAlt="Forgot password illustration"
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">{t('ForgotPasswordForm.headerTitle')}</h1>
        <p className="text-balance text-sm text-muted-foreground">
          {t('ForgotPasswordForm.headerDescription')}
        </p>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="flex flex-col gap-6">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('ForgotPasswordForm.email')}</FormLabel>
                  <FormControl>
                    <Input placeholder="email@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={isPending} className="w-full">
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('ForgotPasswordForm.resetLoading')}
                </>
              ) : (
                <>
                  {t('ForgotPasswordForm.resetButton')}
                  <Mail className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
          <div className="mt-4 text-center text-sm">
            <Link to="/auth/login" className="underline underline-offset-4">
              {t('ForgotPasswordForm.backToLogin')}
            </Link>
          </div>
        </form>
      </Form>
    </AuthForm>
  );
};